import dagre from 'dagre';

import type { WorkflowEdge, WorkflowNode } from '@repo/shared-types';

let nodeCounter = 0;

export const generateNodeId = (): string => {
  nodeCounter += 1;
  return `node_${Date.now()}_${nodeCounter}`;
};

export const getStartLabel = (triggerType: string): string => {
  switch (triggerType) {
    case 'dealerkit':
      return 'Lead Received';
    case 'any':
      return 'Start';
    case 'scheduled':
      return 'Scheduled';
    case 'webhook':
      return 'Webhook Received';
    case 'event':
      return 'Event Fired';
    case 'call':
    default:
      return 'Call Ended';
  }
};

export const DEFAULT_START_NODE: WorkflowNode = {
  id: 'start',
  type: 'start',
  position: { x: 50, y: 200 },
  data: { label: 'Call Ended' },
};

/**
 * Per-node-type palette.
 *
 * The canvas card is a neutral square — like n8n, a node is identified by its
 * icon, not by a coloured box — so `icon` (the glyph colour) and `tint` (the
 * soft square behind it) carry the type. `handle` colours the connector dots.
 * `bg`/`border`/`text` remain for anything still using the old tinted chip.
 */
export const NODE_COLORS: Record<
  string,
  {
    bg: string;
    border: string;
    text: string;
    tint: string;
    icon: string;
    handle: string;
  }
> = {
  start: {
    bg: 'bg-green-50',
    border: 'border-green-400',
    text: 'text-green-700',
    tint: 'bg-green-100 dark:bg-green-500/15',
    icon: 'text-green-600 dark:text-green-400',
    handle: '!bg-green-500',
  },
  condition: {
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    text: 'text-amber-700',
    tint: 'bg-amber-100 dark:bg-amber-500/15',
    icon: 'text-amber-600 dark:text-amber-400',
    handle: '!bg-amber-500',
  },
  send_email: {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    text: 'text-blue-700',
    tint: 'bg-blue-100 dark:bg-blue-500/15',
    icon: 'text-blue-600 dark:text-blue-400',
    handle: '!bg-blue-500',
  },
  send_sms: {
    bg: 'bg-purple-50',
    border: 'border-purple-400',
    text: 'text-purple-700',
    tint: 'bg-purple-100 dark:bg-purple-500/15',
    icon: 'text-purple-600 dark:text-purple-400',
    handle: '!bg-purple-500',
  },
  update_call: {
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    text: 'text-orange-700',
    tint: 'bg-orange-100 dark:bg-orange-500/15',
    icon: 'text-orange-600 dark:text-orange-400',
    handle: '!bg-orange-500',
  },
  enrich_data: {
    bg: 'bg-teal-50',
    border: 'border-teal-400',
    text: 'text-teal-700',
    tint: 'bg-teal-100 dark:bg-teal-500/15',
    icon: 'text-teal-600 dark:text-teal-400',
    handle: '!bg-teal-500',
  },
  run_review: {
    bg: 'bg-rose-50',
    border: 'border-rose-400',
    text: 'text-rose-700',
    tint: 'bg-rose-100 dark:bg-rose-500/15',
    icon: 'text-rose-600 dark:text-rose-400',
    handle: '!bg-rose-500',
  },
  agent: {
    bg: 'bg-slate-50',
    border: 'border-slate-400',
    text: 'text-slate-700',
    tint: 'bg-slate-100 dark:bg-slate-500/15',
    icon: 'text-slate-600 dark:text-slate-400',
    handle: '!bg-slate-500',
  },
  run_classifier: {
    bg: 'bg-pink-50',
    border: 'border-pink-400',
    text: 'text-pink-700',
    tint: 'bg-pink-100 dark:bg-pink-500/15',
    icon: 'text-pink-600 dark:text-pink-400',
    handle: '!bg-pink-500',
  },
  run_quality: {
    bg: 'bg-fuchsia-50',
    border: 'border-fuchsia-400',
    text: 'text-fuchsia-700',
    tint: 'bg-fuchsia-100 dark:bg-fuchsia-500/15',
    icon: 'text-fuchsia-600 dark:text-fuchsia-400',
    handle: '!bg-fuchsia-500',
  },
  call_api: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-400',
    text: 'text-indigo-700',
    tint: 'bg-indigo-100 dark:bg-indigo-500/15',
    icon: 'text-indigo-600 dark:text-indigo-400',
    handle: '!bg-indigo-500',
  },
  update_contact: {
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    text: 'text-amber-700',
    tint: 'bg-amber-100 dark:bg-amber-500/15',
    icon: 'text-amber-600 dark:text-amber-400',
    handle: '!bg-amber-500',
  },
  booking: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-400',
    text: 'text-cyan-700',
    tint: 'bg-cyan-100 dark:bg-cyan-500/15',
    icon: 'text-cyan-600 dark:text-cyan-400',
    handle: '!bg-cyan-500',
  },
  outbound_call: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    text: 'text-emerald-700',
    tint: 'bg-emerald-100 dark:bg-emerald-500/15',
    icon: 'text-emerald-600 dark:text-emerald-400',
    handle: '!bg-emerald-500',
  },
  get_analytics: {
    bg: 'bg-sky-50',
    border: 'border-sky-400',
    text: 'text-sky-700',
    tint: 'bg-sky-100 dark:bg-sky-500/15',
    icon: 'text-sky-600 dark:text-sky-400',
    handle: '!bg-sky-500',
  },
  get_quality_issues: {
    bg: 'bg-violet-50',
    border: 'border-violet-400',
    text: 'text-violet-700',
    tint: 'bg-violet-100 dark:bg-violet-500/15',
    icon: 'text-violet-600 dark:text-violet-400',
    handle: '!bg-violet-500',
  },
  collect: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-400',
    text: 'text-cyan-700',
    tint: 'bg-cyan-100 dark:bg-cyan-500/15',
    icon: 'text-cyan-600 dark:text-cyan-400',
    handle: '!bg-cyan-500',
  },
  campaign: {
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    text: 'text-orange-700',
    tint: 'bg-orange-100 dark:bg-orange-500/15',
    icon: 'text-orange-600 dark:text-orange-400',
    handle: '!bg-orange-500',
  },
  handoff: {
    bg: 'bg-teal-50',
    border: 'border-teal-400',
    text: 'text-teal-700',
    tint: 'bg-teal-100 dark:bg-teal-500/15',
    icon: 'text-teal-600 dark:text-teal-400',
    handle: '!bg-teal-500',
  },
  stock: {
    bg: 'bg-lime-50',
    border: 'border-lime-400',
    text: 'text-lime-700',
    tint: 'bg-lime-100 dark:bg-lime-500/15',
    icon: 'text-lime-600 dark:text-lime-400',
    handle: '!bg-lime-500',
  },
  function: {
    bg: 'bg-stone-50',
    border: 'border-stone-400',
    text: 'text-stone-700',
    tint: 'bg-stone-100 dark:bg-stone-500/15',
    icon: 'text-stone-600 dark:text-stone-400',
    handle: '!bg-stone-500',
  },
  create_payment: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-400',
    text: 'text-yellow-700',
    tint: 'bg-yellow-100 dark:bg-yellow-500/15',
    icon: 'text-yellow-600 dark:text-yellow-400',
    handle: '!bg-yellow-500',
  },
  tracking_link: {
    bg: 'bg-sky-50',
    border: 'border-sky-400',
    text: 'text-sky-700',
    tint: 'bg-sky-100 dark:bg-sky-500/15',
    icon: 'text-sky-600 dark:text-sky-400',
    handle: '!bg-sky-500',
  },
  notification: {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    text: 'text-blue-700',
    tint: 'bg-blue-100 dark:bg-blue-500/15',
    icon: 'text-blue-600 dark:text-blue-400',
    handle: '!bg-blue-500',
  },
  end: {
    bg: 'bg-red-50',
    border: 'border-red-400',
    text: 'text-red-700',
    tint: 'bg-red-100 dark:bg-red-500/15',
    icon: 'text-red-600 dark:text-red-400',
    handle: '!bg-red-500',
  },
  return: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    text: 'text-emerald-700',
    tint: 'bg-emerald-100 dark:bg-emerald-500/15',
    icon: 'text-emerald-600 dark:text-emerald-400',
    handle: '!bg-emerald-500',
  },
};

export const getDefaultNodeData = (type: string): Record<string, unknown> => {
  switch (type) {
    case 'condition':
      return {
        label: 'New Condition',
        field: '',
        operator: 'equals',
        value: '',
        rules: [],
        logic: 'AND',
      };
    case 'send_email':
      return { label: 'Send Email', to: '', subject: '', body: null, templateId: null };
    case 'send_sms':
      return { label: 'Send SMS', to: '', body: null, templateId: null, defaultCountryCode: '+44' };
    case 'update_call':
      return { label: 'Update Call', mappings: [] };
    case 'enrich_data':
      return { label: 'Enrich Data', action: '', input: '', outputVariable: '' };
    case 'agent':
      // outputKey deliberately defaults off the canonical 'classification' /
      // 'quality' names — calls_with_review joins only those, so any other name
      // keeps an agent node's output off live dashboards.
      return {
        label: 'Agent',
        prompt: '',
        outputSchema: { type: 'object', properties: {}, required: [] },
        outputKey: 'agent',
        includeAudio: false,
        includeTranscript: true,
        includeOriginalTranscript: false,
        persistTo: 'none',
        version: 'agent-v1',
      };
    case 'run_review':
      return { label: 'AI Review', reviewConfigId: '', outputKey: 'review' };
    case 'run_classifier':
      return { label: 'Classifier', classifierConfigId: '', outputKey: 'classification' };
    case 'run_quality':
      return { label: 'Quality', qualityConfigId: '', outputKey: 'quality' };
    case 'call_api':
      return {
        label: 'Call API',
        method: 'GET',
        url: '',
        headers: [],
        body: null,
        outputKey: 'api_response',
      };
    case 'update_contact':
      return { label: 'Contact', mappings: [], dataBody: null };
    case 'booking':
      return { label: 'Booking', action: 'add', date: '', time: '', title: '', description: '' };
    case 'outbound_call':
      return {
        label: 'Outbound Call',
        configurationId: '',
        to: '{{contact.phone}}',
        fromNumber: '',
        defaultCountryCode: '+44',
      };
    case 'get_analytics':
      return {
        label: 'Get Analytics',
        dateFrom: '{{date_offset:0}}',
        dateTo: '{{date_offset:0}}',
        channel: 'ALL',
        mode: 'calls',
        outputKey: 'analytics',
      };
    case 'get_quality_issues':
      return {
        label: 'Quality Issues',
        dateFrom: '{{date_offset:-1}}',
        dateTo: '{{date_offset:-1}}',
        minSeverity: 'major',
        outputKey: 'quality_issues',
        detailField: 'summary',
      };
    case 'collect':
      return {
        label: 'Collect',
        entity: 'bookings',
        filters: [],
        orderDirection: 'asc',
        limit: 200,
        outputKey: 'collected',
      };
    case 'campaign':
      return {
        label: 'Create Campaign',
        itemsKey: 'collected',
        configurationId: '',
        name: '',
        channel: 'PSTN',
        contactName: '{{item.name}}',
        contactPhone: '{{item.phone}}',
        activate: true,
        outputKey: 'campaign',
      };
    case 'handoff':
      return {
        label: 'AI Handoff',
        configurationId: '',
        expiresHours: 48,
        outputKey: 'handoff',
      };
    case 'stock':
      return {
        label: 'Stock Lookup',
        action: 'search',
        searchBy: 'registration',
        searchValue: '',
        outputKey: 'stock',
      };
    case 'create_payment':
      return {
        label: 'Create Payment',
        vehicleName: '{{call.lead_data.selected_vehicle.name}}',
        vehicleRegistration: '{{call.lead_data.selected_vehicle.registration}}',
        amount: '',
        phoneNumber: '{{call.phone_number}}',
        markAsSent: false,
        outputKey: 'payment',
      };
    case 'tracking_link':
      return {
        label: 'Tracking Link',
        url: '',
        linkType: 'WORKFLOW_VEHICLE_LINK',
        outputKey: 'tracking',
      };
    case 'function':
      return {
        label: 'Function',
        code: '',
        input: '',
        outputKey: 'function_result',
        timeoutMs: 5000,
      };
    case 'notification':
      return { label: 'Notification', title: '', body: '', to: '' };
    case 'end':
      return { label: 'End' };
    case 'return':
      return { label: 'Return', body: '{}' };
    default:
      return { label: 'Node' };
  }
};

export const autoLayout = (nodes: WorkflowNode[], edges: WorkflowEdge[]): WorkflowNode[] => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 120 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: 200, height: 80 });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, position: { x: pos.x - 100, y: pos.y - 40 } };
  });
};
